import { Dayjs } from 'dayjs';
import { FormattedMessage } from 'react-intl';
import { PickerChangeHandlerContext, TimePicker, TimePickerProps, TimeValidationError } from '@mui/x-date-pickers';
import { ReactElement, ReactNode, useState } from 'react';
import { makeStyles } from '@mui/styles';

import { PoolTextfield } from '../pool-textfield';

const useStyles = makeStyles(() => ({
    timePickerList: {
        '& .MuiList-root': {
            minWidth: 'fit-content',
        },
    },
}));

export interface PoolTimePickerNewProps extends TimePickerProps<Dayjs> {
    name?: string;
    required?: boolean;
    helperText?: ReactNode;
    error?: boolean;
    fullWidth?: boolean;
    onBlur?: () => void;
}

export const PoolTimePicker = (props: PoolTimePickerNewProps): ReactElement => {
    const { name, required, slotProps, helperText, error, onBlur, fullWidth = true, ...datePickerProps } = props;
    const classes = useStyles();
    const [pickerError, setPickerError] = useState<TimeValidationError>(null);

    const handleChange = (value: Dayjs | null, context: PickerChangeHandlerContext<TimeValidationError>): void => {
        setPickerError(context.validationError);
        props.onChange?.(value, context);
    };

    const getHelperText = (): ReactNode => {
        if (helperText) {
            return helperText;
        }

        if (pickerError && !props.disabled && !props.readOnly) {
            return <FormattedMessage id={'components.datePicker.' + pickerError} />;
        }
        return '';
    };

    const handleError = (error: TimeValidationError, value: Dayjs | null): void => {
        props.onError?.(error, value);
        setPickerError(error);
    };

    return (
        <TimePicker
            {...datePickerProps}
            className={classes.timePickerList}
            slots={{
                textField: PoolTextfield,
            }}
            slotProps={{
                ...slotProps,
                textField: {
                    ...slotProps?.textField,
                    name,
                    required,
                    helperText: getHelperText(),
                    hint: getHelperText(),
                    fullWidth,
                    onBlur: onBlur?.(),
                    error,
                    // We need to cast to any, because 'hint' is not part of the MUI API
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
                popper: {
                    ...slotProps?.popper,
                    placement: 'bottom-start',
                },
                actionBar: {
                    actions: [],
                },
            }}
            onError={handleError}
            onChange={handleChange}
        />
    );
};
